import { Box, Spinner } from '@factorialco/deprecated-design-system'
import {
  ContractsContractPoliciesFragment,
  ContractsCustomFieldsPoliciesFragment,
  ContractsEmployeesContractVersionFragment,
  ContractsEmployeesEmployeeFragment,
  ContractsEmployeesJobCatalogContextlessPoliciesFragment,
  ContractsEmployeesReferenceContractFragment,
  JobCatalogTreeFragment,
  UpdateFlowDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'

import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'

import UpdateFlowContent from './_UpdateFlowContent'

const Loading = () => {
  return (
    <Box width="full" height="full" justifyContent="center" alignItems="center">
      <Spinner />
    </Box>
  )
}

interface Props {
  jobCatalogTrees?: JobCatalogTreeFragment[]
  jobCatalogTreeFeatureEnabled?: boolean
  contractVersion?: ContractsEmployeesReferenceContractFragment | null
  contractVersions: ContractsEmployeesContractVersionFragment[]
  employee: ContractsEmployeesEmployeeFragment
  hasDevTimeTrackingWorkableDaysDefinition?: boolean
  hasTimeTrackingAnnualisationDailyValue?: boolean
  jobCatalogContextlessPolicies: ContractsEmployeesJobCatalogContextlessPoliciesFragment | undefined
  contractVersionPayrollPolicyId?: number
  hasPayrollBundle: boolean
  contractContextlessPolicies: ContractsContractPoliciesFragment
  customFieldsContextlessPolicies: ContractsCustomFieldsPoliciesFragment | undefined
}

const UpdateFlow = ({
  jobCatalogTrees,
  jobCatalogTreeFeatureEnabled,
  contractVersion,
  contractVersions,
  employee: graphqlEmployee,
  hasDevTimeTrackingWorkableDaysDefinition,
  hasTimeTrackingAnnualisationDailyValue,
  jobCatalogContextlessPolicies,
  contractVersionPayrollPolicyId,
  hasPayrollBundle,
  contractContextlessPolicies,
  customFieldsContextlessPolicies,
}: Props) => {
  const { setCustomFields, setCustomValues } = useContractFlow()
  const { data, loading: loadingUpdateFlow } = useQuery(UpdateFlowDocument, {
    variables: {
      contractVersionId: contractVersion?.id ?? 0,
      hasContractVersion: !!contractVersion,
      legalEntityId: contractVersion?.employee?.legalEntity?.id ?? 0,
      companyId: graphqlEmployee.company.id,
    },
  })
  const customFields = (
    data?.customFields.resourceFieldsConnection.edges.map((edge) => edge.node.field) ?? []
  ).filter(Boolean)
  const customValues = data?.contracts?.contractVersion?.values ?? []

  useEffect(() => {
    if (!loadingUpdateFlow) {
      setCustomFields(customFields)
      setCustomValues(customValues)
    }
  }, [loadingUpdateFlow])

  if (loadingUpdateFlow) return <Loading />

  const hasFixedDiscontinuousContracts =
    data?.features?.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_fixed_discontinuous_contracts'
    )?.enabled ?? false

  return (
    <UpdateFlowContent
      customFields={customFields}
      customValues={customValues}
      jobCatalogTrees={jobCatalogTrees}
      jobCatalogTreeFeatureEnabled={jobCatalogTreeFeatureEnabled}
      contractVersion={contractVersion}
      contractVersions={contractVersions}
      employee={graphqlEmployee}
      hasDevTimeTrackingWorkableDaysDefinition={hasDevTimeTrackingWorkableDaysDefinition}
      hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
      hasFixedDiscontinuousContracts={hasFixedDiscontinuousContracts}
      jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
      contractVersionPayrollPolicyId={contractVersionPayrollPolicyId}
      hasPayrollBundle={hasPayrollBundle}
      contractContextlessPolicies={contractContextlessPolicies}
      customFieldsContextlessPolicies={customFieldsContextlessPolicies}
    />
  )
}

export default observer(UpdateFlow)
